/**
 * HUD, phone and modals.
 *
 * The phone is the player's interface to everything that is not physical:
 * jobs, clients, the bank, the business, the map and goals. Anything that IS
 * physical — working a yard, buying a mower, sleeping, eating — happens by
 * walking to the place and pressing E, deliberately, so the world stays the
 * game rather than becoming a backdrop for menus.
 *
 * The HUD carries only what the player needs while moving: money, the clock,
 * the current objective, and what pressing E would do. Everything else lives on
 * the phone (VISUAL_AUDIT V-06).
 */
import type { GameFacade, InteractionView, LeadView } from '../../sim/facade/GameFacade.js';
import type { SimEvent } from '../../sim/core/events.js';
import type { WorldLocation } from '../../sim/data/world.js';
import { formatMoney, formatMoneyShort } from '../../sim/core/money.js';
import { injectStyles } from './styles.js';
import { LOCATIONS, ROADS, WORLD_BOUNDS } from '../../sim/data/world.js';
import { BALANCE } from '../../sim/data/balance.js';
import { MANUAL_SLOTS, AUTOSAVE_SLOT } from '../../sim/systems/SaveSystem.js';

type Tab = 'jobs' | 'clients' | 'business' | 'bank' | 'map' | 'goals' | 'saves';

/**
 * What each part of a yard job is called, and what to do about it.
 *
 * There is no tool-switch control: the tool follows where the player is
 * standing, so the hint has to say what that means, or the change from one
 * activity to another looks like the game deciding things on its own.
 */
const TASK_LABELS: Record<string, string> = {
  mow: 'Grass', trim: 'Edges', clear: 'Clearing',
};
const TASK_HINTS: Record<string, string> = {
  mow: 'Walk the mower over the grass. What you leave uncut, they will see.',
  trim: 'Walk the boundary to trim the edges - the trimmer comes out on its own.',
  clear: 'Walk onto each heap to clear it.',
};

export interface UiHooks {
  onRequestPointerLock(): void;
  onReleasePointerLock(): void;
  /** A celebration should be felt as well as seen. */
  onCelebrate?(): void;
  /** Sound cues. Optional so the UI works with audio disabled. */
  sound?: {
    ui(kind: 'click' | 'open' | 'close' | 'error'): void;
    money(kind: 'earn' | 'spend' | 'big'): void;
    milestone(): void;
    toggleMute(): boolean;
    isMuted(): boolean;
  };
}

export interface Ui {
  root: HTMLElement;
  update(near: WorldLocation | null): void;
  /** Returns true when the key was consumed by the UI. */
  handleKey(code: string): boolean;
  interact(): void;
  isModalOpen(): boolean;
  /** Dismiss the phone, shop and day summary. Used by pause and by capture. */
  closeAll(): void;
  setPointerLocked(locked: boolean): void;
  setHintsVisible(visible: boolean): void;
  /**
   * The drive prompt and the fuel gauge.
   *
   * Separate from the world interaction prompt because both can be relevant
   * at once - the car is parked outside the front door - and merging them
   * would leave the player guessing which one a key press would hit.
   */
  setDrivePrompt(mode: 'none' | 'enter' | 'exit', fuelFraction: number): void;
  /** A doorway prompt: the label to show, or null for none. */
  setDoorPrompt(label: string | null): void;
  /** Whether `interact()` would do anything right now. Drives the touch button. */
  hasInteraction(): boolean;
}

const TAB_META: Record<Tab, { label: string; glyph: string }> = {
  jobs: { label: 'Work', glyph: '🧰' },
  clients: { label: 'Booked', glyph: '📋' },
  business: { label: 'Business', glyph: '🏷️' },
  bank: { label: 'Money', glyph: '💳' },
  map: { label: 'Map', glyph: '🗺️' },
  goals: { label: 'Goals', glyph: '🎯' },
  saves: { label: 'Saves', glyph: '💾' },
};

/** One character each, so the clock line stays a clock line. */
const WEATHER_GLYPHS: Record<string, string> = {
  sunny: '☀', cloudy: '☁', light_rain: '🌧',
};

/** Milestones worth stopping the screen for, with the line to show. */
const CELEBRATIONS: Record<string, { eyebrow: string; headline: string; sub: string }> = {
  first_dollar: { eyebrow: 'First earnings', headline: 'You made your first dollar', sub: 'It starts here.' },
  first_tool: { eyebrow: 'Equipped', headline: 'You own your first tool', sub: 'Now you can take real work.' },
  nw_500: { eyebrow: 'Milestone', headline: '$500 net worth', sub: 'Rent is no longer terrifying.' },
  registered: { eyebrow: 'Registered', headline: 'You have a business', sub: 'Contracts and hiring are open.' },
  nw_1000: { eyebrow: 'Milestone', headline: '$1,000 net worth', sub: 'Four figures. Ten times where you started.' },
  nw_2500: { eyebrow: 'Milestone', headline: '$2,500 net worth', sub: 'This is starting to work.' },
  first_hire: { eyebrow: 'Your first employee', headline: 'You are no longer the whole company', sub: 'Someone else is working while you are not.' },
  nw_5000: { eyebrow: 'Milestone', headline: '$5,000 net worth', sub: 'Halfway.' },
  nw_10000: { eyebrow: 'Target reached', headline: '$10,000 net worth', sub: 'You built this from a hundred dollars.' },
};

export function createUi(facade: GameFacade, hooks: UiHooks): Ui {
  injectStyles();

  const root = el('div', 'le-ui');
  let tab: Tab = 'jobs';
  let phoneOpen = false;
  let shopKind: 'tools' | 'food' | null = null;
  let summaryOpen = false;
  let debugOpen = false;
  let currentInteraction: InteractionView = { kind: 'none', locationId: '', label: '' };
  let pendingSummary = false;
  let hintsEnabled = true;
  let lastCash = facade.hud().cash;
  const debugEnabled = isDebugEnabled();
  clickSound = () => hooks.sound?.ui('click');

  // ------------------------------------------------------------ money + clock
  const status = el('div', 'le-status');
  const moneyBox = el('div', 'le-money');
  const moneyAmount = el('div', 'amount');
  const clockBox = el('div', 'clock');
  const clockTime = el('div', 'time');
  const clockDate = el('div', 'date');
  clockBox.append(clockTime, clockDate);
  moneyBox.append(moneyAmount, el('div', 'sep'), clockBox);

  const speedBox = el('div', 'le-speed');
  const speedButtons: HTMLButtonElement[] = [];
  for (const [label, value] of [['❚❚', 0], ['1×', 1], ['2×', 2], ['4×', 4]] as const) {
    const b = el('button') as HTMLButtonElement;
    b.textContent = label;
    b.onclick = () => facade.setSpeed(value);
    b.dataset.speed = String(value);
    b.title = value === 0 ? 'Pause (Space)' : `${value}× speed (${value === 4 ? 3 : value})`;
    speedButtons.push(b);
    speedBox.appendChild(b);
  }
  const muteBtn = el('button') as HTMLButtonElement;
  muteBtn.textContent = '🔊';
  muteBtn.title = 'Mute (M)';
  muteBtn.onclick = () => {
    const nowMuted = hooks.sound?.toggleMute() ?? false;
    muteBtn.textContent = nowMuted ? '🔇' : '🔊';
  };
  speedBox.appendChild(muteBtn);

  status.append(moneyBox, speedBox);
  root.appendChild(status);

  // ------------------------------------------------------------- objective
  const objective = el('div', 'le-objective');
  const objEyebrow = el('div', 'eyebrow');
  objEyebrow.textContent = 'Next goal';
  const objGoal = el('div', 'goal');
  const objHint = el('div', 'hint');
  const objTrack = el('div', 'track');
  const objFill = el('i');
  objTrack.appendChild(objFill);
  const objTrackLabel = el('div', 'track-label');
  const objNow = el('span');
  const objTarget = el('span');
  objTrackLabel.append(objNow, objTarget);
  objective.append(objEyebrow, objGoal, objHint, objTrack, objTrackLabel);
  root.appendChild(objective);

  // --------------------------------------------------------------- vitals
  const vitals = el('div', 'le-vitals');
  const hungerVital = vital('Hunger', '#f2b134');
  const energyVital = vital('Energy', '#6fb4ff');
  const repVital = vital('Rep', '#4fd48a');
  vitals.append(hungerVital.box, energyVital.box, repVital.box);
  root.appendChild(vitals);

  // ---------------------------------------------------------- interaction
  const prompt = el('div', 'le-prompt');
  const promptKey = el('span', 'key');
  promptKey.textContent = 'E';
  const promptLabel = el('span', 'label');
  const promptWhere = el('span', 'where');
  prompt.append(promptKey, promptLabel, promptWhere);
  root.appendChild(prompt);

  const workBox = el('div', 'le-work');
  const workRow = el('div', 'row');
  const workTitle = el('span', 'title');
  const workTime = el('span', 't');
  workRow.append(workTitle, workTime);
  const workBar = el('div', 'bar');
  const workFill = el('i');
  workBar.appendChild(workFill);
  // The work meter is the one the player controls; the clock runs regardless.
  const mowRow = el('div', 'row mow');
  const mowLabel = el('span', 'title');
  mowLabel.textContent = 'Job done';
  const mowValue = el('span', 't');
  mowRow.append(mowLabel, mowValue);
  const mowBar = el('div', 'bar mow');
  const mowFill = el('i');
  mowBar.appendChild(mowFill);
  // One line per part of the job, when there is more than one part. With only
  // a mower this stays empty and the panel is exactly what it was.
  const taskList = el('div', 'le-tasks');
  const workHint = el('div', 'hint');
  workBox.append(workRow, workBar, mowRow, mowBar, taskList, workHint);
  root.appendChild(workBox);

  // ---------------------------------------------------------------- toasts
  const toasts = el('div', 'le-toasts');
  root.appendChild(toasts);

  const celebrate = el('div', 'le-celebrate');
  const celEyebrow = el('div', 'eyebrow');
  const celHeadline = el('div', 'headline');
  const celSub = el('div', 'sub');
  celebrate.append(celEyebrow, celHeadline, celSub, el('div', 'rule'));
  root.appendChild(celebrate);

  // ----------------------------------------------------------------- hints
  const hint = el('div', 'le-hint');
  hint.innerHTML =
    '<b>WASD</b> move · <b>Shift</b> run · <b>Mouse</b> look<br>' +
    '<b>E</b> interact · <b>Tab</b> phone · <b>Space</b> pause · <b>M</b> mute' +
    (debugEnabled ? ' · <b>F1</b> debug' : '');
  root.appendChild(hint);

  // ----------------------------------------------------------------- phone
  const phoneBtn = el('button', 'le-phonebtn') as HTMLButtonElement;
  const phoneGlyph = el('span', 'glyph');
  phoneGlyph.textContent = '📱';
  const phoneBadge = el('span', 'badge');
  phoneBtn.append(phoneGlyph, document.createTextNode('Phone'), phoneBadge);
  phoneBtn.onclick = () => togglePhone();
  root.appendChild(phoneBtn);

  const modal = el('div', 'le-modal');
  const phone = el('div', 'le-phone');
  const phoneHead = el('div', 'le-phone-head');
  const phoneTitleBox = el('div');
  const phoneTitle = el('div', 'title');
  const phoneSub = el('div', 'sub');
  phoneTitleBox.append(phoneTitle, phoneSub);
  const phoneClose = el('button') as HTMLButtonElement;
  phoneClose.textContent = '×';
  phoneClose.title = 'Close (Esc)';
  phoneClose.onclick = () => closeAll();
  phoneHead.append(phoneTitleBox, phoneClose);

  const tabs = el('div', 'le-tabs');
  const tabButtons = new Map<Tab, HTMLButtonElement>();
  for (const id of Object.keys(TAB_META) as Tab[]) {
    const meta = TAB_META[id];
    const b = el('button') as HTMLButtonElement;
    const glyph = el('span', 'glyph');
    glyph.textContent = meta.glyph;
    const label = el('span');
    label.textContent = meta.label;
    b.append(glyph, label);
    b.onclick = () => { tab = id; render(); };
    tabButtons.set(id, b);
    tabs.appendChild(b);
  }
  const phoneBody = el('div', 'le-phone-body');
  phone.append(phoneHead, tabs, phoneBody);
  modal.appendChild(phone);
  modal.addEventListener('mousedown', (e) => { if (e.target === modal) closeAll(); });
  root.appendChild(modal);

  // ------------------------------------------------------------ shop modal
  const shopModal = el('div', 'le-modal');
  const shopPanel = el('div', 'le-phone');
  const shopHead = el('div', 'le-phone-head');
  const shopTitleBox = el('div');
  const shopTitle = el('div', 'title');
  const shopSub = el('div', 'sub');
  shopTitleBox.append(shopTitle, shopSub);
  const shopClose = el('button') as HTMLButtonElement;
  shopClose.textContent = '×';
  shopClose.onclick = () => closeAll();
  shopHead.append(shopTitleBox, shopClose);
  const shopBody = el('div', 'le-phone-body');
  shopPanel.append(shopHead, shopBody);
  shopModal.appendChild(shopPanel);
  shopModal.addEventListener('mousedown', (e) => { if (e.target === shopModal) closeAll(); });
  root.appendChild(shopModal);

  // --------------------------------------------------------- day summary
  const summaryModal = el('div', 'le-modal');
  const summaryPanel = el('div', 'le-summary');
  summaryModal.appendChild(summaryPanel);
  root.appendChild(summaryModal);

  // ------------------------------------------------------------ debug panel
  const debugPanel = el('div', 'le-debug');
  const debugHead = el('h3');
  debugHead.textContent = 'Developer tools';
  debugPanel.appendChild(debugHead);
  const debugGrid = el('div', 'grid');
  const debugActions: [string, () => void][] = [
    ['+$500', () => facade.debug.addMoney(500)],
    ['-$500', () => facade.debug.removeMoney(500)],
    ['+$5,000', () => facade.debug.addMoney(5000)],
    ['+$1k debt', () => facade.debug.addDebt(1000)],
    ['Next day', () => facade.debug.advanceDay()],
    ['08:00', () => facade.debug.setHour(8)],
    ['19:00', () => facade.debug.setHour(19)],
    ['22:00', () => facade.debug.setHour(22)],
    ['Rep +25', () => facade.debug.setReputation(facade.hud().reputation + 25)],
    ['New lead', () => facade.debug.spawnLead()],
    ['Finish job', () => facade.debug.completeActiveJob()],
    ['All tools', () => facade.debug.giveEquipment()],
  ];
  for (const [label, action] of debugActions) {
    const b = el('button') as HTMLButtonElement;
    b.textContent = label;
    b.onclick = () => { action(); render(); };
    debugGrid.appendChild(b);
  }
  debugPanel.appendChild(debugGrid);
  root.appendChild(debugPanel);

  // ------------------------------------------------------------- events
  facade.onEvent(handleEvent);

  function handleEvent(event: SimEvent): void {
    switch (event.type) {
      case 'job.completed':
        toast(event.title, formatMoney(event.pay, { sign: true }), 'good');
        hooks.sound?.money(event.pay >= 5000 ? 'big' : 'earn');
        break;
      case 'employee.completedJob':
        toast(`${event.name} finished a job`, formatMoney(event.pay, { sign: true }), 'good');
        break;
      case 'equipment.purchased':
        toast(`Bought ${event.name}`, formatMoney(-event.price), 'warn');
        hooks.sound?.money('spend');
        break;
      case 'equipment.serviced':
        toast(`Serviced ${event.name}`, formatMoney(-event.cost), 'warn');
        hooks.sound?.money('spend');
        break;
      case 'equipment.worn':
        // Fires once, on the crossing, so it reads as news rather than as
        // the nagging that teaches a player to stop reading toasts.
        toast(
          `${event.name} is wearing out`,
          'Service it at the depot',
          'warn',
        );
        break;
      case 'employee.hired':
        toast(`Hired ${event.name}`, `${formatMoney(event.wage)}/h`, 'good');
        break;
      case 'contract.signed':
        toast(`${event.customerName} signed a contract`, formatMoney(event.weeklyValue), 'good');
        break;
      case 'contract.lost':
        toast(`Lost ${event.customerName}`, event.reason, 'bad');
        break;
      case 'expense.charged':
        toast(event.name, formatMoney(-event.amount), event.paid ? 'warn' : 'bad');
        break;
      case 'lead.expired':
        toast(`${event.customerName} went elsewhere`, 'lead expired', 'bad');
        break;
      case 'milestone.reached':
        showCelebration(event.id, event.label);
        break;
      case 'need.critical':
        toast(
          event.need === 'hunger' ? 'You are getting very hungry' : 'You are exhausted',
          event.need === 'hunger' ? 'Buy food' : 'Get some sleep',
          'bad',
        );
        break;
      case 'housing.upgraded':
      case 'vehicle.purchased':
        toast('Acquired', event.name, 'good');
        break;
      case 'notice':
        toast(
          event.message, '',
          event.level === 'good' ? 'good' : event.level === 'bad' ? 'bad' : 'warn',
        );
        break;
      case 'day.ended':
        // Deliberately NOT shown here. A modal at midnight interrupts whatever
        // the player is doing; the natural end of a day is going to bed, so the
        // summary is raised from the sleep interaction instead.
        pendingSummary = true;
        break;
      default:
        break;
    }
  }

  function toast(title: string, amount: string, kind: 'good' | 'bad' | 'warn'): void {
    const t = el('div', `le-toast ${kind}`);
    const titleNode = el('span', 'title');
    titleNode.textContent = title;
    t.appendChild(titleNode);
    if (amount) {
      const a = el('span', 'amount');
      a.textContent = amount;
      t.appendChild(a);
    }
    toasts.appendChild(t);
    // Cap the stack; a busy day can emit a lot at once.
    while (toasts.children.length > 5) toasts.removeChild(toasts.firstChild!);
    setTimeout(() => {
      t.classList.add('leaving');
      setTimeout(() => t.remove(), 260);
    }, 4600);
  }

  /**
   * A milestone is the emotional beat of this game's progression; the V1 build
   * emitted the same small toast for reaching $10,000 as for rent being charged
   * (VISUAL_AUDIT V-16).
   */
  function showCelebration(id: string, fallback: string): void {
    const copy = CELEBRATIONS[id] ?? { eyebrow: 'Milestone', headline: fallback, sub: '' };
    celEyebrow.textContent = copy.eyebrow;
    celHeadline.textContent = copy.headline;
    celSub.textContent = copy.sub;
    celebrate.classList.remove('show');
    // Force a reflow so the animation restarts even on back-to-back milestones.
    void celebrate.offsetWidth;
    celebrate.classList.add('show');
    hooks.onCelebrate?.();
    hooks.sound?.milestone();
  }

  // ------------------------------------------------------------- rendering
  function update(near: WorldLocation | null): void {
    const hud = facade.hud();

    moneyAmount.textContent = formatMoney(hud.cash);
    moneyAmount.classList.toggle('negative', hud.cash <= 0);
    if (hud.cash !== lastCash) {
      lastCash = hud.cash;
      moneyAmount.classList.remove('flash');
      void moneyAmount.offsetWidth;
      moneyAmount.classList.add('flash');
    }
    clockTime.textContent = hud.clock;
    clockDate.textContent =
      `${hud.dayName} · Day ${hud.day} · ${WEATHER_GLYPHS[hud.weather] ?? ''} ${hud.weatherLabel}`;

    for (const b of speedButtons) {
      const value = Number(b.dataset.speed);
      b.classList.toggle('on', hud.paused ? value === 0 : value === hud.speed);
    }

    hungerVital.set(hud.hunger);
    energyVital.set(hud.energy);
    repVital.set(hud.reputation, `${Math.round(hud.reputation)}`);

    // Objective
    const next = facade.nextObjective();
    objGoal.textContent = next.label;
    objHint.textContent = next.hint;
    objFill.style.width = `${(next.progress * 100).toFixed(1)}%`;
    objNow.textContent = next.now;
    objTarget.textContent = next.target;

    const pending = hud.newLeads;
    phoneBadge.textContent = pending > 0 ? String(pending) : '';
    phoneBadge.style.display = pending > 0 ? '' : 'none';
    const jobsTab = tabButtons.get('jobs');
    if (jobsTab) {
      let dot = jobsTab.querySelector('.dot');
      if (pending > 0 && !dot) {
        dot = el('span', 'dot');
        jobsTab.appendChild(dot);
      } else if (pending === 0 && dot) {
        dot.remove();
      }
    }

    // Interaction prompt.
    currentInteraction = facade.interactionAt(near?.id ?? null);
    const showPrompt =
      currentInteraction.kind !== 'none' && !isModalOpen() && hud.activeWork === null;
    prompt.classList.toggle('show', showPrompt);
    if (showPrompt) {
      promptLabel.textContent = currentInteraction.label;
      promptWhere.textContent = near ? `· ${near.name}` : '';
    }

    // Active work.
    workBox.classList.toggle('show', hud.activeWork !== null);
    if (hud.activeWork) {
      const coverage = hud.activeWork.coverage;
      workTitle.textContent = hud.activeWork.title;
      workTime.textContent = `${hud.activeWork.minutesRemaining} min remaining`;
      workFill.style.width = `${(hud.activeWork.progress * 100).toFixed(1)}%`;
      mowValue.textContent = `${Math.round(coverage * 100)}%`;
      mowFill.style.width = `${(coverage * 100).toFixed(1)}%`;
      mowBar.classList.toggle('low', coverage < 0.5);

      const tasks = hud.activeWork.tasks;
      // The breakdown only earns its space when there is a choice about where
      // to spend the next minute. One task and it is the bar above, restated.
      if (tasks.length > 1) {
        taskList.innerHTML = tasks.map((t) => {
          const done = Math.round(t.progress * 100);
          return `<span class="${t.progress > 0.97 ? 'done' : ''}">`
            + `${TASK_LABELS[t.kind] ?? t.kind} ${done}%</span>`;
        }).join('');
      } else if (taskList.childElementCount > 0) {
        taskList.replaceChildren();
      }

      const unfinished = tasks.find((t) => t.progress <= 0.97);
      workHint.textContent = !unfinished
        ? 'The whole job is done. Finish the clock and collect.'
        : TASK_HINTS[unfinished.kind] ?? '';
    }

    // The controls hint fades once the player has clearly got the idea.
    if (hintsEnabled) {
      hint.classList.toggle(
        'faded', hud.day > 1 || facade.sim.state.statistics.jobsCompleted > 0,
      );
    }

    if (phoneOpen || shopKind) render();
  }

  function render(): void {
    if (shopKind) {
      renderShop();
      return;
    }
    if (!phoneOpen) return;
    const hud = facade.hud();
    phoneTitle.textContent = TAB_META[tab].label;
    phoneSub.textContent = `${hud.dayName} ${hud.clock} · ${formatMoney(hud.cash)}`;
    for (const [id, button] of tabButtons) button.classList.toggle('on', id === tab);
    phoneBody.replaceChildren(...renderTab());
  }

  function renderTab(): HTMLElement[] {
    switch (tab) {
      case 'jobs': return renderJobs();
      case 'clients': return renderClients();
      case 'business': return renderBusiness();
      case 'bank': return renderBank();
      case 'map': return renderMap();
      case 'goals': return renderGoals();
      case 'saves': return renderSaves();
    }
  }

  function leadCard(lead: LeadView, best: boolean): HTMLElement {
    const item = el('div', `le-item${best ? ' best' : ''}`);
    const head = el('div', 'head');
    const name = el('div', 'name');
    name.textContent = lead.customerName;
    const pay = el('div', 'pay');
    pay.textContent = formatMoney(lead.payout);
    head.append(name, pay);

    const meta = el('div', 'meta');
    // Everything the player judges a job on sits next to the size and the
    // address, which is where they are already looking. Behind a tap it would
    // be read after the decision rather than before it. That now covers three
    // kinds of fact: what the customer is like, whether the player knows
    // them, and which district it is in - a repeat customer nobody can
    // recognise is not a relationship, and an address is only a travel
    // decision if you know where it is.
    const relationTag = lead.relationship === 'loyal'
      ? `<span class="tag loyal">Loyal · ${lead.jobsForCustomer} jobs</span>`
      : lead.relationship === 'regular'
        ? `<span class="tag regular">Regular · ${lead.jobsForCustomer} jobs</span>`
        : lead.relationship === 'returning'
          ? '<span class="tag returning">Been here before</span>'
          : '';
    const tierTag = lead.tier === 'wealthy'
      ? '<span class="tag wealthy">Hillcrest</span>'
      : lead.tier === 'middle'
        ? '<span class="tag middle">Uptown</span>'
        : '';
    meta.innerHTML =
      (best ? '<span class="tag best">Best rate</span>' : '') +
      relationTag + tierTag +
      lead.tags.map((t) => `<span class="tag trait">${t}</span>`).join('') +
      `${sizeWord(lead.lotSize)} yard · ${lead.locationName}`;

    const facts = el('div', 'facts');
    facts.append(
      fact('Time', `~${lead.estimatedMinutes} min`),
      fact('Distance', `${lead.distanceMetres} m`),
      fact('Per hour', formatMoney(lead.ratePerHour), true),
      fact('Expires', `${lead.hoursUntilExpiry} h`),
    );

    item.append(head, meta, facts);
    return item;
  }

  function renderJobs(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const leads = facade.leads().filter((l) => !l.accepted);

    out.push(section('Yard leads'));
    if (leads.length === 0) {
      out.push(empty('🌱', 'No new leads right now.\nDo good work and more will come in.'));
    } else {
      // Best value first — the interesting choice is rate, not raw payout.
      const sorted = [...leads].sort((a, b) => b.ratePerHour - a.ratePerHour);
      for (const [index, lead] of sorted.entries()) {
        const item = leadCard(lead, index === 0 && sorted.length > 1);
        const actions = el('div', 'le-actions');
        actions.append(
          button('Accept', 'primary', () => { facade.acceptLead(lead.id); render(); }),
          button('Decline', '', () => { facade.declineLead(lead.id); render(); }),
        );
        item.append(actions);
        out.push(item);
      }
    }

    // What the rest of the town is waiting on.
    //
    // Only shown while something is still shut, and it names the specific
    // lock rather than saying "keep playing". A player watching their lead
    // board stop growing otherwise has to guess between reputation and a
    // vehicle, and guessing wrong is the difference between a week of
    // grinding and saving $900.
    const districts = facade.districts().filter((d) => !d.open);
    if (districts.length > 0) {
      out.push(section('Not calling you yet'));
      for (const d of districts) {
        const item = el('div', 'le-item locked');
        const head = el('div', 'head');
        const name = el('div', 'name');
        name.textContent = d.name;
        const tag = el('div', 'pay');
        tag.textContent = d.blockedBy === 'vehicle' ? 'Needs wheels' : 'Needs a name';
        head.append(name, tag);
        const meta = el('div', 'meta');
        meta.textContent = d.blockedBy === 'vehicle'
          ? (d.needVehicle === 'car'
            ? 'You are not getting a mower up here on a bicycle. Buy a car.'
            : 'Too far to walk with a mower. Even a bicycle would do it.')
          : `They will call once you have ${d.needReputation} reputation.`;
        item.append(head, meta);
        out.push(item);
      }
    }

    const gigs = facade.gigs();
    out.push(section('Courier gigs'));
    if (gigs.length === 0) {
      out.push(empty('📦', 'No parcels waiting.\nMore appear through the day.'));
    } else {
      for (const gig of gigs) {
        const item = el('div', 'le-item');
        const head = el('div', 'head');
        const name = el('div', 'name');
        name.textContent = gig.title;
        const pay = el('div', 'pay');
        pay.textContent = formatMoney(gig.pay);
        head.append(name, pay);
        const meta = el('div', 'meta');
        meta.innerHTML = `Collect at ${gig.pickupName}<br>Deliver to ${gig.dropoffName}`;
        const actions = el('div', 'le-actions');
        actions.append(button('Run it', '', () => {
          facade.travelTo(gig.pickupId);
          facade.travelTo(gig.dropoffId);
          facade.completeGig(gig.id);
          render();
        }));
        item.append(head, meta, actions);
        out.push(item);
      }
    }

    out.push(section('Depot shift'));
    const canShift = facade.canWorkShift();
    const shiftItem = el('div', 'le-item');
    const shiftHead = el('div', 'head');
    const shiftName = el('div', 'name');
    shiftName.textContent = 'Warehouse shift · 4 hours';
    const shiftPay = el('div', 'pay');
    shiftPay.textContent = formatMoney(6000);
    shiftHead.append(shiftName, shiftPay);
    const shiftMeta = el('div', 'meta');
    shiftMeta.textContent = canShift
      ? 'Reliable money, but it costs you the morning.'
      : 'Shifts start between 07:00 and 15:00, one per day.';
    const shiftActions = el('div', 'le-actions');
    const shiftBtn = button('Work a shift', 'primary', () => {
      facade.travelTo('depot');
      facade.workShift();
      render();
    });
    shiftBtn.disabled = !canShift;
    shiftActions.append(shiftBtn);
    shiftItem.append(shiftHead, shiftMeta, shiftActions);
    out.push(shiftItem);

    return out;
  }

  function renderClients(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const accepted = facade.leads().filter((l) => l.accepted);

    out.push(section('Work booked in'));
    if (accepted.length === 0) {
      out.push(empty('📋',
        'Nothing booked.\nAccept leads on the Work tab,\nthen walk to the property and press E.'));
    } else {
      for (const lead of accepted) {
        const item = leadCard(lead, false);
        item.classList.add('accent');
        const tags = el('div', 'meta');
        tags.innerHTML =
          (lead.fromContract ? '<span class="tag contract">Contract</span>' : '') +
          (lead.assigned
            ? '<span class="tag crew">Crew is on it</span>'
            : '<span class="tag accepted">Yours to do</span>');
        item.insertBefore(tags, item.children[1] ?? null);
        out.push(item);
      }
    }
    return out;
  }

  function renderBusiness(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const s = facade.businessSummary();
    const hud = facade.hud();

    if (!s.registered) {
      out.push(section('Register your business'));
      const item = el('div', 'le-item accent');
      const meta = el('div', 'meta');
      meta.innerHTML =
        'Registering unlocks recurring contracts and lets you hire.';
      item.append(meta);
      const facts = el('div', 'facts');
      facts.append(
        fact('Fee', formatMoney(s.registrationFee)),
        fact('Reputation needed', `${s.registrationMinReputation}`),
        fact('You have', hud.reputation.toFixed(0), hud.reputation >= s.registrationMinReputation),
      );
      item.append(facts);
      const nameInput = el('input', 'le-input') as HTMLInputElement;
      nameInput.placeholder = 'Business name';
      nameInput.value = 'Grove Yard Care';
      const wrap = el('div');
      wrap.style.marginTop = '11px';
      wrap.append(nameInput);
      item.append(wrap);
      const actions = el('div', 'le-actions');
      const go = button('Register', 'primary', () => {
        facade.registerBusiness(nameInput.value);
        render();
      });
      go.disabled = !s.canRegister;
      actions.append(go);
      item.append(actions);
      out.push(item);
      return out;
    }

    out.push(section('Your operation'));
    const headline = el('div', 'le-bigstat');
    const n = el('span', 'n');
    n.textContent = s.name;
    n.style.fontSize = '20px';
    headline.append(n);
    out.push(headline);

    const stats = el('div');
    stats.append(
      stat('Work speed', `${s.speedMultiplier.toFixed(2)}×`),
      stat('Job quality', `${Math.round(s.qualityBase * 100)}%`),
      stat('Equipment', s.equipmentNames.join(', ') || 'None', 'dim'),
      stat('Spare crew kits', String(Math.max(0, s.crewKits - hud.employees))),
      stat('Weekly wages', formatMoney(s.weeklyWageBill), s.weeklyWageBill > 0 ? 'bad' : 'dim'),
      stat('Weekly expenses', formatMoney(s.weeklyExpenses), 'bad'),
      stat('Lifetime revenue', formatMoney(s.lifetimeRevenue), 'good'),
    );
    out.push(stats);

    out.push(section('Your crew'));
    const crew = facade.employees();
    if (crew.length === 0) {
      out.push(empty('👷',
        'No employees yet.\nA crew works while you do other things —\nbut wages are owed whether there is work or not.'));
    } else {
      for (const e of crew) {
        const item = el('div', 'le-item');
        const head = el('div', 'head');
        const name = el('div', 'name');
        name.textContent = e.name + (e.absentToday ? ' · away today' : '');
        const pay = el('div', 'pay');
        pay.textContent = `${formatMoney(e.hourlyWage)}/h`;
        head.append(name, pay);
        const facts = el('div', 'facts');
        facts.append(
          fact('Skill', `${Math.round(e.skill * 100)}%`),
          fact('Jobs done', String(e.jobsCompleted)),
          fact('Unpaid wages', formatMoney(e.accruedWages)),
        );
        const actions = el('div', 'le-actions');
        actions.append(button('Let go', '', () => { facade.fire(e.id); render(); }));
        item.append(head, facts, actions);
        out.push(item);
      }
    }

    out.push(section('Hiring'));
    const check = s.hireCheck;
    if (!check.ok) {
      const note = el('div', 'le-note warn');
      note.textContent = check.reason ?? '';
      out.push(note);
    }
    for (const a of facade.applicants()) {
      const item = el('div', 'le-item');
      const head = el('div', 'head');
      const name = el('div', 'name');
      name.textContent = a.name;
      const pay = el('div', 'pay');
      pay.textContent = `${formatMoney(a.hourlyWage)}/h`;
      head.append(name, pay);
      const facts = el('div', 'facts');
      facts.append(
        fact('Skill', `${Math.round(a.skill * 100)}%`),
        fact('Weekly cost', formatMoney(a.hourlyWage * 7 * 5), true),
      );
      const actions = el('div', 'le-actions');
      const hireBtn = button('Hire', 'primary', () => { facade.hire(a.id); render(); });
      hireBtn.disabled = !check.ok;
      actions.append(hireBtn);
      item.append(head, facts, actions);
      out.push(item);
    }

    out.push(section('Transport'));
    for (const v of facade.vehicles()) {
      out.push(purchaseCard(
        v.name + (v.owned ? ' · owned' : ''), v.price, v.description,
        v.owned, v.affordable, () => { facade.buyVehicle(v.id); render(); },
      ));
    }

    out.push(section('Housing'));
    for (const h of facade.housingOptions()) {
      out.push(purchaseCard(
        h.name + (h.current ? ' · current' : ''), h.deposit,
        `${h.description}<br>Rent ${formatMoney(h.weeklyRent)} per week`,
        h.current, h.affordable, () => { facade.moveHouse(h.id); render(); },
        'Move in',
      ));
    }
    return out;
  }

  function purchaseCard(
    name: string, price: number, description: string,
    owned: boolean, affordable: boolean, onBuy: () => void, verb = 'Buy',
  ): HTMLElement {
    const item = el('div', 'le-item');
    const head = el('div', 'head');
    const n = el('div', 'name');
    n.textContent = name;
    const p = el('div', 'pay');
    p.textContent = formatMoney(price);
    head.append(n, p);
    const meta = el('div', 'meta');
    meta.innerHTML = description;
    item.append(head, meta);
    if (!owned) {
      const actions = el('div', 'le-actions');
      const b = button(verb, '', onBuy);
      b.disabled = !affordable;
      actions.append(b);
      item.append(actions);
    }
    return item;
  }

  function renderBank(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const hud = facade.hud();

    const headline = el('div', 'le-bigstat');
    const n = el('span', `n ${hud.netWorth >= 0 ? 'good' : 'bad'}`);
    n.textContent = formatMoney(hud.netWorth);
    const u = el('span', 'u');
    u.textContent = 'net worth';
    headline.append(n, u);
    out.push(headline);

    const stats = el('div');
    stats.append(
      stat('Cash', formatMoney(hud.cash), 'good'),
      stat('Bank', formatMoney(hud.bank)),
      stat('Debt', formatMoney(hud.debt), hud.debt > 0 ? 'bad' : 'dim'),
    );
    out.push(stats);

    out.push(section('Move money'));
    const actionsRow = el('div', 'le-actions');
    actionsRow.append(
      button('Deposit $100', '', () => { facade.deposit(10000); render(); }),
      button('Withdraw $100', '', () => { facade.withdraw(10000); render(); }),
    );
    out.push(actionsRow);

    out.push(section('Credit'));
    const creditNote = el('div', 'le-note');
    creditNote.textContent =
      `You can borrow up to ${formatMoney(facade.maxLoan())} more. Interest is charged weekly.`;
    out.push(creditNote);
    const creditRow = el('div', 'le-actions');
    const borrow = button('Borrow $300', '', () => { facade.takeLoan(30000); render(); });
    borrow.disabled = facade.maxLoan() < 30000;
    const repay = button('Repay $100', '', () => { facade.repayDebt(10000); render(); });
    repay.disabled = hud.debt <= 0 || hud.cash < 10000;
    creditRow.append(borrow, repay);
    out.push(creditRow);

    out.push(section('Recent transactions'));
    const tx = facade.transactions(20);
    if (tx.length === 0) {
      out.push(empty('🧾', 'No transactions yet.'));
    } else {
      const list = el('div');
      for (const t of tx) {
        list.append(stat(
          `Day ${t.day} · ${t.label}`,
          formatMoney(t.amount, { sign: t.amount > 0 }),
          t.amount > 0 ? 'good' : 'bad',
        ));
      }
      out.push(list);
    }
    return out;
  }

  function renderMap(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const active = facade.activeJobLocations();
    out.push(section('The town'));

    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    canvas.style.cssText = 'width:100%;border-radius:12px;border:1px solid var(--line);display:block;';
    drawMap(canvas, facade, active);
    out.push(canvas as unknown as HTMLElement);

    const legend = el('div', 'le-note le-legend');
    legend.innerHTML = [
      ['#ffd23f', '●', 'job waiting'],
      ['#4fd48a', '▲', 'you'],
      ['#4fd48a', '■', 'your room'],
      ['#6fb4ff', '■', 'shops &amp; services'],
      ['#6f7a86', '■', 'houses'],
      ['#8d7fa8', '■', 'Hillcrest'],
    ].map(([colour, glyph, label]) =>
      `<span><i style="color:${colour};font-style:normal">${glyph}</i> ${label}</span>`,
    ).join('');
    out.push(legend);

    out.push(section('Places'));
    const list = el('div');
    for (const l of LOCATIONS.filter((x) => x.kind !== 'customer')) {
      list.append(stat(l.name, l.description ?? '', 'dim'));
    }
    out.push(list);
    return out;
  }

  function renderGoals(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const hud = facade.hud();

    const headline = el('div', 'le-bigstat');
    const n = el('span', 'n');
    n.textContent = formatMoneyShort(hud.netWorth);
    const u = el('span', 'u');
    u.textContent = `of ${formatMoneyShort(1_000_000)} net worth`;
    headline.append(n, u);
    out.push(headline);

    const bar = el('div', 'track');
    bar.style.cssText = 'height:8px;background:rgba(255,255,255,.08);border-radius:4px;overflow:hidden;margin:10px 0 4px;';
    const fill = el('i');
    fill.style.cssText =
      `display:block;height:100%;width:${(hud.goalProgress * 100).toFixed(1)}%;` +
      'background:linear-gradient(90deg,#6fb4ff,#ffd23f);border-radius:4px;';
    bar.appendChild(fill);
    out.push(bar);

    out.push(section('Milestones'));
    const list = el('div');
    for (const m of facade.milestones()) {
      list.append(stat(
        `${m.done ? '✓' : '○'}  ${m.label}`,
        m.done ? 'done' : '',
        m.done ? 'good' : 'dim',
      ));
    }
    out.push(list);
    return out;
  }

  function renderSaves(): HTMLElement[] {
    const out: HTMLElement[] = [];
    const existing = new Map(facade.listSaves().map((s) => [s.slot, s]));

    out.push(section('Save slots'));
    for (const slot of [...MANUAL_SLOTS, AUTOSAVE_SLOT]) {
      const meta = existing.get(slot);
      const item = el('div', 'le-item');
      const head = el('div', 'head');
      const name = el('div', 'name');
      name.textContent = slot === AUTOSAVE_SLOT ? 'Autosave' : `Slot ${slot.slice(-1)}`;
      head.append(name);
      const info = el('div', 'meta');
      info.textContent = meta
        ? meta.corrupt
          ? 'Unreadable save file'
          : `Day ${meta.day} ${meta.clock} · ${formatMoney(meta.cash)} · ${meta.businessName}`
        : 'Empty';
      const actions = el('div', 'le-actions');
      if (slot !== AUTOSAVE_SLOT) {
        actions.append(button('Save', 'primary', () => {
          facade.save(slot);
          toast('Game saved', slot, 'good');
          render();
        }));
      }
      const loadBtn = button('Load', '', () => {
        const result = facade.load(slot);
        toast(result.ok ? 'Game loaded' : 'Could not load', result.error ?? '',
          result.ok ? 'good' : 'bad');
        closeAll();
      });
      loadBtn.disabled = !meta || meta.corrupt;
      actions.append(loadBtn);
      if (meta) {
        actions.append(button('Delete', '', () => { facade.deleteSave(slot); render(); }));
      }
      item.append(head, info, actions);
      out.push(item);
    }
    return out;
  }

  function renderShop(): void {
    const hud = facade.hud();
    if (shopKind === 'tools') {
      shopTitle.textContent = 'Hardline Tool Depot';
      shopSub.textContent = `You have ${formatMoney(hud.cash)}`;
      const nodes: HTMLElement[] = [section('Equipment')];
      for (const item of facade.toolShop()) {
        const card = el('div', `le-item${!item.owned && item.affordable && !item.locked ? '' : ''}`);
        const head = el('div', 'head');
        const name = el('div', 'name');
        name.textContent = item.name + (item.owned ? ' · owned' : '');
        const pay = el('div', 'pay');
        pay.textContent = formatMoney(item.price);
        head.append(name, pay);
        const meta = el('div', 'meta');
        meta.textContent = item.description;
        card.append(head, meta);

        if (item.owned && item.condition !== null) {
          // Condition as a bar rather than a percentage. What the player
          // needs to know is "is this still the tool I paid for", and a bar
          // answers that at a glance where a number asks to be compared
          // against a threshold nobody told them.
          const row = el('div', 'row cond');
          const label = el('span', 'title');
          label.textContent = 'Condition';
          const value = el('span', 't');
          value.textContent = `${Math.round(item.condition * 100)}%`;
          row.append(label, value);
          const bar = el('div', 'bar cond');
          if (item.needsService) bar.classList.add('low');
          const fill = el('i');
          fill.style.width = `${(item.condition * 100).toFixed(0)}%`;
          bar.appendChild(fill);
          card.append(row, bar);

          if (item.serviceCost > 0) {
            const note = el('div', `le-note${item.needsService ? ' warn' : ''}`);
            note.textContent = item.needsService
              ? 'Worn enough to be costing you speed and finish.'
              : 'A service would bring it back to new.';
            card.append(note);
            const actions = el('div', 'le-actions');
            const b = button(
              `Service · ${formatMoney(item.serviceCost)}`,
              item.needsService ? 'primary' : '',
              () => { facade.serviceEquipment(item.id); render(); },
            );
            b.disabled = item.serviceCost > hud.cash;
            actions.append(b);
            card.append(actions);
          }
        }

        if (!item.owned) {
          const facts = el('div', 'facts');
          if (item.speedGain > 0) facts.append(fact('Speed', `+${(item.speedGain * 100).toFixed(0)}%`, true));
          if (item.qualityGain > 0) facts.append(fact('Quality', `+${(item.qualityGain * 100).toFixed(0)}%`));
          if (facts.children.length) card.append(facts);
          if (item.lockReason) {
            const note = el('div', 'le-note warn');
            note.textContent = item.lockReason;
            card.append(note);
          }
          const actions = el('div', 'le-actions');
          const b = button('Buy', 'primary', () => { facade.buyEquipment(item.id); render(); });
          b.disabled = !item.affordable || item.locked;
          actions.append(b);
          card.append(actions);
        }
        nodes.push(card);
      }
      shopBody.replaceChildren(...nodes);
    } else if (shopKind === 'food') {
      shopTitle.textContent = 'Grove Corner Store';
      shopSub.textContent = `You have ${formatMoney(hud.cash)}`;
      const nodes: HTMLElement[] = [section('Food and drink')];
      for (const f of facade.food()) {
        const card = el('div', 'le-item');
        const head = el('div', 'head');
        const name = el('div', 'name');
        name.textContent = f.name;
        const pay = el('div', 'pay');
        pay.textContent = formatMoney(f.price);
        head.append(name, pay);
        const facts = el('div', 'facts');
        facts.append(fact('Hunger', `+${f.hunger}`, true));
        if (f.energy) facts.append(fact('Energy', `+${f.energy}`));
        const actions = el('div', 'le-actions');
        const b = button('Buy and eat', 'primary', () => { facade.eat(f.id); render(); });
        b.disabled = !f.affordable;
        actions.append(b);
        card.append(head, facts, actions);
        nodes.push(card);
      }
      shopBody.replaceChildren(...nodes);
    }
  }

  function showDaySummary(): void {
    const s = facade.lastDaySummary();
    if (!s || s.day < 1) return;
    summaryOpen = true;
    hooks.onReleasePointerLock();
    const positive = s.net >= 0;
    summaryPanel.replaceChildren();
    const h = el('h2');
    h.textContent = `Day ${s.day}`;
    const sub = el('div', 'sub');
    sub.textContent = positive ? 'You ended the day up.' : 'You ended the day down.';
    const net = el('div', `net ${positive ? 'good' : 'bad'}`);
    net.textContent = formatMoney(s.net, { sign: positive });
    const stats = el('div');
    stats.append(
      stat('Earned', formatMoney(s.earned), 'good'),
      stat('Spent', formatMoney(s.spent), 'bad'),
      stat('Jobs you did', String(s.jobsDone)),
      stat('Jobs your crew did', String(s.employeeJobsDone)),
      stat('Cash on hand', formatMoney(s.cashEnd)),
      stat('Net worth', formatMoney(s.netWorthEnd)),
      stat('Reputation', s.reputation.toFixed(1)),
    );
    const go = el('button') as HTMLButtonElement;
    go.textContent = 'Start the next day';
    go.onclick = () => { summaryOpen = false; summaryModal.classList.remove('show'); };
    summaryPanel.append(h, sub, net, stats, go);
    summaryModal.classList.add('show');
  }

  // ------------------------------------------------------------- behaviour
  function togglePhone(): void {
    phoneOpen = !phoneOpen;
    hooks.sound?.ui(phoneOpen ? 'open' : 'close');
    modal.classList.toggle('show', phoneOpen);
    if (phoneOpen) {
      shopKind = null;
      shopModal.classList.remove('show');
      hooks.onReleasePointerLock();
      render();
    }
  }

  function openShop(kind: 'tools' | 'food'): void {
    shopKind = kind;
    hooks.sound?.ui('open');
    phoneOpen = false;
    modal.classList.remove('show');
    shopModal.classList.add('show');
    hooks.onReleasePointerLock();
    renderShop();
  }

  function openPhone(target: Tab): void {
    phoneOpen = true;
    tab = target;
    modal.classList.add('show');
    hooks.onReleasePointerLock();
    render();
  }

  function closeAll(): void {
    phoneOpen = false;
    shopKind = null;
    summaryOpen = false;
    modal.classList.remove('show');
    shopModal.classList.remove('show');
    summaryModal.classList.remove('show');
  }

  function interact(): void {
    if (isModalOpen()) return;
    switch (currentInteraction.kind) {
      case 'sleep': {
        const before = facade.hud().day;
        facade.sleep();
        if (pendingSummary || facade.hud().day > before) {
          pendingSummary = false;
          showDaySummary();
        }
        break;
      }
      case 'shop_tools':
        openShop('tools');
        break;
      case 'shop_food':
        openShop('food');
        break;
      case 'bank':
        openPhone('bank');
        break;
      case 'depot':
        openPhone('jobs');
        break;
      case 'job_site':
        if (currentInteraction.leadId) facade.startWork(currentInteraction.leadId);
        break;
      default:
        break;
    }
  }

  function handleKey(code: string): boolean {
    if (code === 'Escape') {
      if (isModalOpen()) { closeAll(); return true; }
      return false;
    }
    if (code === 'Tab') { togglePhone(); return true; }
    if (code === 'Space') {
      if (!isModalOpen()) { facade.togglePause(); return true; }
      return false;
    }
    if (code === 'KeyM' && !isModalOpen()) {
      const nowMuted = hooks.sound?.toggleMute() ?? false;
      muteBtn.textContent = nowMuted ? '🔇' : '🔊';
      return true;
    }
    if (code === 'F1' && debugEnabled) {
      debugOpen = !debugOpen;
      debugPanel.classList.toggle('show', debugOpen);
      return true;
    }
    if (code === 'Digit1' && !isModalOpen()) { facade.setSpeed(1); return true; }
    if (code === 'Digit2' && !isModalOpen()) { facade.setSpeed(2); return true; }
    if (code === 'Digit3' && !isModalOpen()) { facade.setSpeed(4); return true; }
    return false;
  }

  function isModalOpen(): boolean {
    return phoneOpen || shopKind !== null || summaryOpen;
  }

  const drivePrompt = document.createElement('div');
  drivePrompt.className = 'le-drive';
  drivePrompt.innerHTML =
    '<span class="key">F</span><span class="label"></span>' +
    '<span class="fuel"><i></i></span>';
  root.appendChild(drivePrompt);
  const driveLabel = drivePrompt.querySelector('.label') as HTMLElement;
  const fuelBar = drivePrompt.querySelector('.fuel i') as HTMLElement;
  let driveMode: 'none' | 'enter' | 'exit' = 'none';

  function setDrivePrompt(mode: 'none' | 'enter' | 'exit', fuelFraction: number): void {
    if (mode !== driveMode) {
      driveMode = mode;
      drivePrompt.classList.toggle('show', mode !== 'none');
      driveLabel.textContent = mode === 'exit' ? 'Get out' : 'Drive';
    }
    if (mode !== 'none') {
      const pct = Math.max(0, Math.min(1, fuelFraction)) * 100;
      fuelBar.style.width = `${pct.toFixed(0)}%`;
      // Amber under a fifth, red under a twentieth. A fuel gauge that only
      // reads empty when it is empty is a gauge you check too late.
      fuelBar.style.background = pct < 5 ? '#ff6b5c' : pct < 20 ? '#f2b134' : '#57d18a';
    }
  }

  const doorPrompt = document.createElement('div');
  doorPrompt.className = 'le-door';
  doorPrompt.innerHTML = '<span class="key">E</span><span class="label"></span>';
  root.appendChild(doorPrompt);
  const doorLabelEl = doorPrompt.querySelector('.label') as HTMLElement;
  let shownDoor: string | null = null;

  function setDoorPrompt(label: string | null): void {
    if (label === shownDoor) return;
    shownDoor = label;
    doorPrompt.classList.toggle('show', label !== null);
    if (label) doorLabelEl.textContent = label;
  }

  function setPointerLocked(locked: boolean): void {
    hint.classList.toggle('faded', locked);
  }

  return {
    root, update, handleKey, interact, isModalOpen, setPointerLocked, closeAll,
    hasInteraction: () => currentInteraction.kind !== 'none' && !isModalOpen(),
    setDrivePrompt,
    setDoorPrompt,
    setHintsVisible(value: boolean): void {
      hintsEnabled = value;
      hint.style.display = value ? '' : 'none';
    },
  };
}

/**
 * Debug tools are development-only and must never be part of normal gameplay.
 * They are enabled by the dev server, or explicitly by `?debug=1`.
 */
function isDebugEnabled(): boolean {
  try {
    if (new URLSearchParams(window.location.search).has('debug')) return true;
  } catch { /* ignore */ }
  return import.meta.env?.DEV === true;
}

// ---------------------------------------------------------------- helpers

function el(tag: string, className = ''): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function section(text: string): HTMLElement {
  const s = el('div', 'le-section');
  s.textContent = text;
  return s;
}

function empty(glyph: string, text: string): HTMLElement {
  const e = el('div', 'le-empty');
  const g = el('span', 'glyph');
  g.textContent = glyph;
  e.append(g, document.createTextNode(text));
  return e;
}

let clickSound: (() => void) | null = null;

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', className) as HTMLButtonElement;
  b.textContent = label;
  b.onclick = () => { clickSound?.(); onClick(); };
  return b;
}

function stat(key: string, value: string, tone = ''): HTMLElement {
  const row = el('div', 'le-stat');
  const k = el('span', 'k');
  k.textContent = key;
  const v = el('span', `v ${tone}`);
  v.textContent = value;
  row.append(k, v);
  return row;
}

function fact(key: string, value: string, highlight = false): HTMLElement {
  const box = el('div', 'fact');
  const k = el('span', 'fk');
  k.textContent = key;
  const v = el('span', `fv${highlight ? ' hi' : ''}`);
  v.textContent = value;
  box.append(k, v);
  return box;
}

function vital(label: string, colour: string) {
  const box = el('div', 'le-vital');
  const k = el('div', 'k');
  const name = el('span');
  name.textContent = label;
  const value = el('b');
  k.append(name, value);
  const bar = el('div', 'bar');
  const fill = el('i');
  fill.style.background = colour;
  bar.appendChild(fill);
  box.append(k, bar);
  return {
    box,
    set(v: number, text?: string) {
      const pct = Math.max(0, Math.min(100, v));
      fill.style.width = `${pct.toFixed(0)}%`;
      value.textContent = text ?? `${Math.round(v)}`;
      fill.style.background = pct < 25 ? '#ff6b5c' : colour;
      box.classList.toggle('low', pct < 25);
    },
  };
}

function sizeWord(size: 'small' | 'medium' | 'large'): string {
  return size === 'small' ? 'Small' : size === 'medium' ? 'Medium' : 'Large';
}

/** Simple top-down map of the neighbourhood, drawn from the shared world data. */
function drawMap(
  canvas: HTMLCanvasElement,
  facade: GameFacade,
  active: Set<string>,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const size = canvas.width;
  // Derived from the world rather than written down here.
  //
  // These were hard-coded numbers - a 215 m span and a six-entry list of
  // streets - which was survivable while the town never changed and became a
  // straightforward bug the moment it did: the map showed Maple Grove and
  // nothing else, with the player marker sliding off the edge somewhere over
  // Hillcrest. Reading WORLD_BOUNDS and ROADS means the map cannot go stale
  // again.
  const margin = 14;
  const span = Math.max(
    WORLD_BOUNDS.maxX - WORLD_BOUNDS.minX,
    WORLD_BOUNDS.maxZ - WORLD_BOUNDS.minZ,
  ) + margin * 2;
  const scale = size / span;
  const toX = (x: number) => (x - WORLD_BOUNDS.minX + margin) * scale;
  const toY = (z: number) => (z - WORLD_BOUNDS.minZ + margin) * scale;

  ctx.fillStyle = '#1d2f20';
  ctx.fillRect(0, 0, size, size);

  ctx.lineCap = 'round';
  // Casing then carriageway, the way a real map draws a road. One flat stroke
  // reads as a scribble; two read as a street. Widths track the road's own,
  // so the ring road and the avenues read as the routes they are.
  for (const [casing, factor] of [[true, 1.25], [false, 0.8]] as const) {
    ctx.strokeStyle = casing ? '#2b2e33' : '#4a4e55';
    for (const road of ROADS) {
      ctx.lineWidth = Math.max(casing ? 3 : 2, road.width * scale * factor);
      ctx.beginPath();
      ctx.moveTo(toX(road.x1), toY(road.z1));
      ctx.lineTo(toX(road.x2), toY(road.z2));
      ctx.stroke();
    }
  }

  // Districts you cannot work in yet are drawn dimmed rather than hidden.
  // Seeing Hillcrest and not being able to go there is the point; a map that
  // simply omitted it would turn the aspiration into a surprise.
  const reputation = facade.sim.state.player.reputation;
  for (const l of LOCATIONS) {
    const highlighted = active.has(l.id);
    const locked = l.tier !== undefined
      && reputation < BALANCE.yard.tierMinReputation[l.tier];
    ctx.globalAlpha = locked && !highlighted ? 0.4 : 1;
    ctx.fillStyle = highlighted
      ? '#ffd23f'
      : l.kind === 'home' ? '#4fd48a'
      : l.kind === 'customer'
        ? (l.tier === 'wealthy' ? '#8d7fa8' : l.tier === 'middle' ? '#6f8a84' : '#6f7a86')
      : l.kind === 'park' ? '#37692f'
      : '#6fb4ff';
    const w = Math.max(5, l.width * scale);
    const h = Math.max(5, l.depth * scale);
    ctx.fillRect(toX(l.x) - w / 2, toY(l.z) - h / 2, w, h);
    if (highlighted) {
      ctx.strokeStyle = 'rgba(255,210,63,.5)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(toX(l.x), toY(l.z), 12, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;

  // District names, so the tags on a job card mean somewhere on the map.
  ctx.font = '700 11px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const [label, x, z, tier] of DISTRICT_LABELS) {
    const locked = reputation < BALANCE.yard.tierMinReputation[tier];
    ctx.globalAlpha = locked ? 0.5 : 0.82;
    ctx.lineWidth = 3.4;
    ctx.strokeStyle = 'rgba(10,14,20,.9)';
    ctx.strokeText(label, toX(x), toY(z));
    ctx.fillStyle = locked ? '#8f9aa8' : '#d6e2ee';
    ctx.fillText(label, toX(x), toY(z));
  }
  ctx.globalAlpha = 1;

  // Labels for the places the player has to find. Customer lots are deliberately
  // unlabelled: twelve house numbers would bury the map they sit on.
  ctx.font = '600 10px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const l of LOCATIONS) {
    if (l.kind === 'customer') continue;
    const label = MAP_LABELS[l.kind] ?? l.name;
    const x = toX(l.x);
    const y = toY(l.z) + Math.max(5, l.depth * scale) / 2 + 9;
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(10,14,20,.9)';
    ctx.strokeText(label, x, y);
    ctx.fillStyle = l.kind === 'home' ? '#8fe8b6' : '#cfd8e4';
    ctx.fillText(label, x, y);
  }

  // The player as an arrowhead, so the map answers "which way am I facing?"
  const p = facade.sim.state.player;
  const px = toX(p.x);
  const py = toY(p.z);
  ctx.save();
  ctx.translate(px, py);
  // A dark disc under the arrow, so the player marker survives sitting on the
  // park's own green.
  ctx.beginPath();
  ctx.arc(0, 0, 9, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(8,12,17,.75)';
  ctx.fill();
  ctx.rotate(-p.facing);
  ctx.beginPath();
  ctx.moveTo(0, -8);
  ctx.lineTo(5.5, 6);
  ctx.lineTo(0, 3.5);
  ctx.lineTo(-5.5, 6);
  ctx.closePath();
  ctx.fillStyle = '#4fd48a';
  ctx.fill();
  ctx.strokeStyle = '#0b0f14';
  ctx.lineWidth = 1.6;
  ctx.stroke();
  ctx.restore();
}

/**
 * Where each district's name is written on the map.
 *
 * Placed in clear ground between the streets rather than derived from the
 * lots' centroid, which lands on top of the houses it is meant to name.
 */
const DISTRICT_LABELS: [string, number, number, 'starter' | 'middle' | 'wealthy'][] = [
  ['MAPLE GROVE', -30, -50, 'starter'],
  ['FAIRVIEW', -30, 112, 'middle'],
  ['LINDEN', -148, 22, 'middle'],
  ['HILLCREST', 123, 140, 'wealthy'],
];

/** Short map labels. The full names belong in the list below the map. */
const MAP_LABELS: Record<string, string> = {
  home: 'Your room',
  toolshop: 'Tool Depot',
  store: 'Store',
  bank: 'Bank',
  depot: 'Logistics',
  park: 'Maple Green',
};
